import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Globe, CreditCard, Plus, Settings, LogOut, ShieldCheck } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useT, useI18n } from '@/i18n/I18nProvider'
import { apiClient } from '@/lib/api'

export function Layout() {
  const t = useT()
  const { locale, setLocale } = useI18n()
  const navigate = useNavigate()
  const signOut = () => {
    localStorage.removeItem('discover.jwt')
    localStorage.removeItem('discover.jwt.admin')
    navigate('/auth/signin')
  }

  // Probe the admin gate once per page-load. /admin/overview returns 403 for
  // non-internal tenants, 200 for internal ones. We only render the Admin nav
  // entry when this succeeds, so non-admins never see it.
  const isAdmin = useQuery({
    queryKey: ['layout-admin-gate'],
    queryFn: () => apiClient.get<unknown>('/admin/overview').then(() => true).catch(() => false),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  // If we got here via impersonation, expose a "return to admin" affordance.
  const adminToken = localStorage.getItem('discover.jwt.admin')
  const returnToAdmin = () => {
    if (adminToken) {
      localStorage.setItem('discover.jwt', adminToken)
      localStorage.removeItem('discover.jwt.admin')
      location.href = '/admin'
    }
  }

  return (
    <div className="min-h-full grid grid-cols-[240px_1fr]">
      <aside
        className="border-r p-4 flex flex-col gap-4"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <Link to="/" className="flex items-center gap-2 px-2 py-1 font-semibold">
          <span>{t('app.name')}</span>
        </Link>
        <nav className="flex flex-col gap-1">
          <NavItem to="/" icon={<LayoutDashboard size={16} />} label={t('nav.dashboard')} />
          <NavItem to="/sites" icon={<Globe size={16} />} label={t('nav.sites')} />
          <NavItem to="/billing" icon={<CreditCard size={16} />} label={t('nav.billing')} />
          <NavItem to="/onboard" icon={<Plus size={16} />} label={t('nav.onboard')} />
          <NavItem to="/settings" icon={<Settings size={16} />} label={t('nav.settings')} />
          {isAdmin.data && (
            <NavItem to="/admin" icon={<ShieldCheck size={16} />} label={t('nav.admin')} />
          )}
        </nav>
        <div className="mt-auto flex flex-col gap-3 text-xs">
          {adminToken && (
            <button
              type="button"
              onClick={returnToAdmin}
              className="px-2 py-1.5 rounded border text-left"
              style={{ borderColor: 'var(--color-warn)', color: 'var(--color-warn)' }}
              title={t('admin.impersonation.return.tooltip')}
            >
              {t('admin.impersonation.return.cta')}
            </button>
          )}
          <button
            type="button"
            onClick={signOut}
            className="flex items-center gap-2 px-2 py-1.5 rounded opacity-80 hover:opacity-100 text-left"
          >
            <LogOut size={14} />
            <span>{t('settings.security.sessions.signout_all')}</span>
          </button>
          <div>
            <label className="block opacity-70 mb-1">Language</label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value as 'en' | 'fr' | 'es')}
              className="border rounded px-2 py-1 w-full bg-transparent"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <option value="en">{t('common.locale.en')}</option>
              <option value="fr">{t('common.locale.fr')}</option>
              <option value="es">{t('common.locale.es')}</option>
            </select>
          </div>
        </div>
      </aside>
      <main className="p-6 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}

function NavItem({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2 py-1.5 rounded text-sm ${isActive ? 'bg-black/5 dark:bg-white/5 font-medium' : 'opacity-80 hover:opacity-100'}`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  )
}
